import { useCallback, useMemo, useRef, useState } from "react";

export default function useUndoRedo(initialValue, { limit = 50 } = {}) {

    const historyRef = useRef({});

    const [scope, setScope] = useState("default");

    const ensureScope = useCallback((scopeKey) => {
        if (!historyRef.current[scopeKey]) {
            historyRef.current[scopeKey] = {
                past: [],
                future: [],
            };
        }

        return historyRef.current[scopeKey];
    }, []);

    const [presentMap, setPresentMap] = useState({
        default: initialValue,
    });

    const present = presentMap[scope];

    const set = useCallback((nextValue, { pushHistory = true } = {}) => {

        setPresentMap(currentMap => {

            const current = currentMap[scope];

            const next =
                typeof nextValue === "function"
                    ? nextValue(current)
                    : nextValue;

            const history = ensureScope(scope);

            if (pushHistory) {
                history.past.push(structuredClone(current));

                if (history.past.length > limit) {
                    history.past.shift();
                }

                history.future = [];
            }

            return {
                ...currentMap,
                [scope]: next,
            };
        });

    }, [scope, limit, ensureScope]);

    const undo = useCallback(() => {

        const history = ensureScope(scope);

        setPresentMap(currentMap => {

            const current = currentMap[scope];

            if (history.past.length === 0) {
                return currentMap;
            }

            const previous = history.past.pop();

            history.future.push(structuredClone(current));

            return {
                ...currentMap,
                [scope]: previous,
            };
        });

    }, [scope, ensureScope]);

    const redo = useCallback(() => {

        const history = ensureScope(scope);

        setPresentMap(currentMap => {

            const current = currentMap[scope];

            if (history.future.length === 0) {
                return currentMap;
            }

            const next = history.future.pop();

            history.past.push(structuredClone(current));

            return {
                ...currentMap,
                [scope]: next,
            };
        });

    }, [scope, ensureScope]);

    const reset = useCallback((value, targetScope = scope) => {

        historyRef.current[targetScope] = {
            past: [],
            future: [],
        };

        setPresentMap(current => ({
            ...current,
            [targetScope]: value,
        }));

    }, [scope]);

    const canUndo = useMemo(() => {
        return ensureScope(scope).past.length > 0;
    }, [scope, ensureScope, present]);

    const canRedo = useMemo(() => {
        return ensureScope(scope).future.length > 0;
    }, [scope, ensureScope, present]);

    return {
        present,
        set,
        undo,
        redo,
        canUndo,
        canRedo,
        reset,
        setScope,
        presentMap,
    };
}
